import type { TranslationKey } from '@/i18n/resources/en.ts';

type SettingsNavigationTranslationKey = Exclude<Extract<TranslationKey, `settings.${string}`>, 'settings.backToContext'>;

export const personalSettingsSections = ['profile', 'people', 'availability', 'notifications', 'language', 'account', 'privacy'] as const;

export type PersonalSettingsSection = (typeof personalSettingsSections)[number];
export type SettingsNavigationId = PersonalSettingsSection | 'business' | 'moderation' | 'admin';

export type SettingsNavigationItem = {
  id: SettingsNavigationId;
  path: string;
  labelKey: SettingsNavigationTranslationKey;
  descriptionKey: SettingsNavigationTranslationKey;
  icon: string;
};

export type SettingsNavigationGroup = {
  id: 'workspace' | 'personal' | 'preferences' | 'account' | 'management';
  labelKey: SettingsNavigationTranslationKey;
  items: SettingsNavigationItem[];
};

export type SettingsNavigationCapabilities = {
  canAccessBusiness: boolean;
  canUsePersonalActivity: boolean;
  isModerator: boolean;
  isAdmin: boolean;
};

const personalItems: Record<PersonalSettingsSection, SettingsNavigationItem> = {
  profile: {
    id: 'profile',
    path: '/settings/profile',
    labelKey: 'settings.profile',
    descriptionKey: 'settings.description.profile',
    icon: 'material-symbols:person-outline-rounded',
  },
  people: {
    id: 'people',
    path: '/settings/people',
    labelKey: 'settings.people',
    descriptionKey: 'settings.description.people',
    icon: 'material-symbols:group-outline-rounded',
  },
  availability: {
    id: 'availability',
    path: '/settings/availability',
    labelKey: 'settings.availability',
    descriptionKey: 'settings.description.availability',
    icon: 'material-symbols:calendar-month-outline-rounded',
  },
  notifications: {
    id: 'notifications',
    path: '/settings/notifications',
    labelKey: 'settings.notifications',
    descriptionKey: 'settings.description.notifications',
    icon: 'material-symbols:notifications-outline-rounded',
  },
  language: {
    id: 'language',
    path: '/settings/language',
    labelKey: 'settings.language',
    descriptionKey: 'settings.description.language',
    icon: 'material-symbols:tune-rounded',
  },
  account: {
    id: 'account',
    path: '/settings/account',
    labelKey: 'settings.account',
    descriptionKey: 'settings.description.account',
    icon: 'material-symbols:shield-person-outline-rounded',
  },
  privacy: {
    id: 'privacy',
    path: '/settings/privacy',
    labelKey: 'settings.privacy',
    descriptionKey: 'settings.description.privacy',
    icon: 'material-symbols:privacy-tip-outline-rounded',
  },
};

export function isPersonalSettingsSection(value: unknown): value is PersonalSettingsSection {
  return typeof value === 'string' && personalSettingsSections.includes(value as PersonalSettingsSection);
}

export function createSettingsNavigation(capabilities: SettingsNavigationCapabilities): SettingsNavigationGroup[] {
  const groups: SettingsNavigationGroup[] = [];

  if (capabilities.canAccessBusiness) {
    groups.push({
      id: 'workspace',
      labelKey: 'settings.group.workspace',
      items: [
        {
          id: 'business',
          path: '/settings/business/overview',
          labelKey: 'settings.business',
          descriptionKey: 'settings.description.business',
          icon: 'mdi:storefront-outline',
        },
      ],
    });
  }

  if (capabilities.canUsePersonalActivity) {
    groups.push(
      { id: 'personal', labelKey: 'settings.group.personal', items: [personalItems.profile, personalItems.people, personalItems.availability] },
      { id: 'preferences', labelKey: 'settings.group.preferences', items: [personalItems.notifications, personalItems.language] },
      { id: 'account', labelKey: 'settings.group.account', items: [personalItems.account, personalItems.privacy] },
    );
  }

  const management: SettingsNavigationItem[] = [];
  if (capabilities.isModerator) {
    management.push({
      id: 'moderation',
      path: '/settings/moderation',
      labelKey: 'settings.moderation',
      descriptionKey: 'settings.description.moderation',
      icon: 'material-symbols:shield-outline',
    });
  }
  if (capabilities.isAdmin) {
    management.push({
      id: 'admin',
      path: '/settings/admin',
      labelKey: 'settings.admin',
      descriptionKey: 'settings.description.admin',
      icon: 'material-symbols:admin-panel-settings-outline',
    });
  }
  if (management.length) groups.push({ id: 'management', labelKey: 'settings.group.management', items: management });

  return groups;
}
